import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Card, Row, Col, Badge, Alert, Spinner, Table } from "react-bootstrap";
import "../../assets/css/Details.css";
import { FaEdit, FaTrashAlt, FaPlus, FaSave, FaArrowLeft, FaFacebookMessenger, FaKey } from "react-icons/fa";
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { driversCompanyAPI } from '../../api/apiClient';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import LocationAutocomplete from "../LocationAutocomplete";

export default function DriverCompanyDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showVehicleEditModal, setShowVehicleEditModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const fromPage = location.state?.fromPage || 1;
  const userId = location.state?.userId;

  const [profileData, setProfileData] = useState({
    id: "",
    firstname: '',
    lastname: '',
    nicname: '',
    company_name: '',
    email: '',
    street_address: '',
    addressone: '',
    postal_code: '',
    title: '',
    telphone: '',
    website: '',
    tax_number: '',
    local_court: '',
    responsible_person: '',
    driver_name: '',
    phone_number: '',
    vehicle_number: '',
    vehicle_type: '',
    max_weight: '',
    max_length: '',
    max_width: '',
    max_height: '',
    average_rating: 0,
    total_reviews: 0,
    rating: []
  });

  const [editData, setEditData] = useState({
    firstname: '',
    lastname: '',
    nicname: '',
    company_name: '',
    email: '',
    street_address: '',
    addressone: '',
    postal_code: '',
    title: '',
    telphone: '',
    website: '',
    tax_number: '',
    local_court: '',
    responsible_person: '',
  });

  const [passwordData, setPasswordData] = useState({
    password: '',
    password_confirmation: ''
  });

  const [vehicleForm, setVehicleForm] = useState({
    driver_name: '',
    phone_number: '',
    vehicle_number: '',
    vehicle_type: '',
    max_weight: '',
    max_length: '',
    max_width: '',
    max_height: ''
  });

  // Fetch driver details
  const fetchDriverDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await driversCompanyAPI.show(id);
      
      console.log('API Response:', response);
      
      if (response.success) {
        const driver = response.data.data;
        console.log('Driver data:', driver);

        setProfileData({
          ...driver,
          website: typeof driver.website === 'object' ? '' : (driver.website || ''),
          tax_number: driver.tax_number || driver.tax_no || '' // handle both field names
        });

        setEditData({
          firstname:  driver.firstname || '',
          lastname:  driver.lastname || '',
          nicname:  driver.nicname || '',
          company_name:  driver.company_name || '',
          email:  driver.email || '',
          street_address:  driver.street_address || '',
          addressone:  driver.addressone || '',
          postal_code:  driver.postal_code || '',
          title:  driver.title || '',
          telphone:  driver.telphone || '',
          website:  driver.website || '',
          tax_number:  driver.tax_number || '',
          local_court:  driver.local_court || '',
          responsible_person:  driver.responsible_person || '',
        });

        setVehicleForm({
          driver_name: driver.driver_name || '',
          phone_number: driver.phone_number || '',
          vehicle_number: driver.vehicle_number || '',
          vehicle_type: driver.vehicle_type || '',
          max_weight: driver.max_weight || '',
          max_length: driver.max_length || '',
          max_width: driver.max_width || '',
          max_height: driver.max_height || ''
        });
      } else {
        setError(response.message || t('failed_to_fetch_driver'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_driver'));
      console.error('Error fetching driver details:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchDriverDetails();
    }
  }, [id]);

  const handleLocationChange = (e) => {
    setEditData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
      ...(e.lat && { [`${e.target.name}_lat`]: e.lat }),
      ...(e.lng && { [`${e.target.name}_lng`]: e.lng })
    }));
  };

  const handleVehicleChange = (e) => {
    const { name, value } = e.target;
    setVehicleForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveVehicle = async () => {
    try {
      setLoading(true);
      
      const dataToSend = { ...vehicleForm };
      Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] === '' || dataToSend[key] == null) {
          delete dataToSend[key];
        }
      });

      const updateData = {
        ...dataToSend,
        firstname: profileData.firstname,
        lastname: profileData.lastname,
        company_name: profileData.company_name,
        email: profileData.email
      };

      console.log('Sending vehicle data:', updateData);

      const response = await driversCompanyAPI.update(id, updateData);
      
      if (response.success) {
        setShowVehicleEditModal(false);
        // تحديث البيانات المحلية
        setProfileData(prev => ({
          ...prev,
          ...vehicleForm
        }));
        
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t('vehicle_updated_successfully'),
          timer: 3000,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t('failed_to_save_vehicle'),
          timer: 3000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t('failed_to_save_vehicle'),
        timer: 3000,
        showConfirmButton: false,
      });
      console.error('Error saving vehicle:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    try {
      if (!passwordData.password || passwordData.password.length < 8) {
        setPasswordError(t("password_must_be_at_least_8_characters"));
        return;
      }

      if (passwordData.password !== passwordData.password_confirmation) {
        setPasswordError(t("passwords_do_not_match"));
        return;
      }

      setLoading(true);
      const response = await driversCompanyAPI.update(id, {
        password: passwordData.password,
        password_confirmation: passwordData.password_confirmation
      });
      
      if (response.success) {
        setShowPasswordModal(false);
        setPasswordData({ password: '', password_confirmation: '' });
        setPasswordError("");
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t('password_updated_successfully'),
          timer: 3000,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t('failed_to_update_password'),
          timer: 3000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t('failed_to_update_password'),
        timer: 3000,
        showConfirmButton: false,
      });
      console.error('Error updating password:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = async () => {
    try {
      setLoading(true);
    
      const dataToSend = Object.fromEntries(
        Object.entries(editData).filter(([key, value]) => 
          value !== null && value !== '' && key !== 'status'
        )
      );
      const response = await driversCompanyAPI.update(id, dataToSend);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t('user_updated_successfully'),
          timer: 3000,
          showConfirmButton: false,
        });
        setShowEditModal(false);
        setProfileData(prev => ({
          ...prev,
          ...editData
        }));
      } else {
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t('failed_to_update_user'),
          timer: 30000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t('failed_to_update_user'),
        timer: 30000,
        showConfirmButton: false,
      });
      console.error('Error updating user:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleBack = () => {
    navigate('/admin/drivers-company', { 
      state: { fromPage: fromPage, scrollToUser: userId } 
    });
  };

  if (loading) {
    return (
      <div className="container py-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="user-profile-page">
      <div className="container-wrapper">
        {/* Header */}
        <div className="custom-header text-white p-4 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
          <div className="d-flex align-items-center">
            <Button 
              variant="light" 
              className="me-3"
              onClick={handleBack}
              title={t('back_to_list')}
            >
              <FaArrowLeft />
            </Button>
            <h2 className="mb-0">{profileData.firstname} {profileData.lastname}</h2>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button
              className="btn btn-light"
              onClick={() => setShowPasswordModal(true)}
            >
              <FaKey className="me-1" />
              {t('change_password')}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setShowEditModal(true)}
            >
              <FaEdit className="me-1" />
              {t('edit_profile')}
            </button>
            <button
              className="btn btn-success"
              onClick={() => navigate(`/admin/driver-company-list/chat-driver-company/${profileData.id}`)}
            >
              <FaFacebookMessenger className="me-1" />
            </button>
          </div>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        {/* Info Cards */}
        <div className="row mt-4">
          <div className="col-md-6 mb-3">
            <div className="card p-3 h-100">
              <p className="mb-1">
                <strong>{profileData.firstname} {profileData.lastname}</strong>
              </p>
              <p className="mb-1"><b>{t('last_name')}: </b>{profileData.nicname || '-'}</p>
              <p className="mb-1"><b>{t('company_name')}: </b>{profileData.company_name || '-'}</p>
              <p className="mb-1"><b>{t('Email')}: </b> {profileData.email || '-'}</p>
              <p className="mb-1"><b>{t('phone')}: </b> {profileData.telphone || '-'}</p>
              <p className="mb-1"><b>{t('website')}: </b> 
                {profileData.website ? (
                  <a href={profileData.website} target="_blank" rel="noopener noreferrer">
                    {profileData.website}
                  </a>
                ) : (
                  '-'
                )}
              </p>
              <p className="mb-1"><b>{t('VAT/Tax_No')}: </b> {profileData.tax_number || '-'}</p>
            </div>
          </div>
          <div className="col-md-6 mb-3">
            <div className="card p-3 h-100">
              <p className="mb-1"><b>{t('local_court')}: </b> {profileData.local_court || '-'}</p>
              <p className="mb-1">
                <b>{t('responsible_person')}: </b> {profileData.responsible_person || '-'}
              </p>
              <p className="mb-1"><b>{t('street')}: </b> {profileData.street_address || '-'}</p>
              <p className="mb-1"><b>{t('addressone')}: </b> {profileData.addressone || '-'}</p>
              <p className="mb-1"><b>{t('postal_code')}: </b> {profileData.postal_code || '-'}</p>
              <p className="mb-1"><b>{t('title')}: </b> {profileData.title || '-'}</p>
            </div>
          </div>
        </div>

        {/* Rating Section */}
        {profileData.total_reviews > 0 && (
          <div className="card mt-4 p-3">
            <h5 className="fw-bold border-bottom pb-2 mb-3">⭐ {t('ratings_reviews')}</h5>
            <div className="row align-items-center">
              <div className="col-md-4 text-center">
                <div className="average-rating display-4 fw-bold text-warning mb-2">
                  {profileData.average_rating || 0}
                </div>
                <div className="rating-stars mb-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={`fs-4 ${star <= Math.round(profileData.average_rating || 0) ? 'text-warning' : 'text-muted'}`}
                    >
                      ★
                    </span>
                  ))}
                </div>
                <div className="total-reviews small text-muted">
                  {t('based_on')} {profileData.total_reviews || 0} {t('reviews')}
                </div>
              </div>
              <div className="col-md-8">
                <div className="rating-breakdown">
                  {[5, 4, 3, 2, 1].map((rating) => {
                    const ratingCount = profileData.rating 
                      ? profileData.rating.filter(r => Math.round(r.rating) === rating).length
                      : 0;
                    
                    const percentage = (ratingCount / (profileData.total_reviews || 1)) * 100;
                    
                    return (
                      <div key={rating} className="rating-bar d-flex align-items-center mb-2">
                        <div className="rating-label me-2" style={{ width: '50px' }}>
                          <span className="text-warning">{rating} ★</span>
                        </div>
                        <div className="progress flex-grow-1 me-2" style={{ height: '8px' }}>
                          <div
                            className="progress-bar bg-warning"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <div className="rating-count small text-muted" style={{ width: '30px' }}>
                          {ratingCount}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Vehicles Table */}
        <div className="card mt-4 p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">🚗 {t('vehicles')}</h5>
          </div>

          <div className="table-responsive">
            <Table bordered hover className="align-middle">
              <thead className="table-dark">
                <tr>
                  <th>{t('driver_name')}</th>
                  <th>{t('phone')}</th>
                  <th>{t('vehicle_number')}</th>
                  <th>{t('vehicle_type')}</th>
                  <th>{t('max_weight')}</th>
                  <th>{t('dimensions')}</th>
                  <th>{t('action')}</th>
                </tr>
              </thead>
              <tbody>
                <tr key={profileData.id}>
                  <td>{profileData.driver_name || '-'}</td>
                  <td>{profileData.phone_number || '-'}</td>
                  <td>{profileData.vehicle_number || '-'}</td>
                  <td>{profileData.vehicle_type || '-'}</td>
                  <td>{profileData.max_weight || '0'} <small>Kg</small></td>
                  <td>{profileData.max_length || '0'} x {profileData.max_width || '0'} x {profileData.max_height || '0'} <small>cm</small></td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => setShowVehicleEditModal(true)}
                      title={t('edit_vehicle')}
                    >
                      <FaEdit />
                    </Button>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        </div>

        {/* Password Modal */}
        <Modal
          show={showPasswordModal}
          onHide={() => setShowPasswordModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{t('change_password')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {passwordError && <Alert variant="danger">{passwordError}</Alert>}
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>{t('new_password')}</Form.Label>
                <Form.Control
                  type="password"
                  value={passwordData.password}
                  onChange={(e) => {
                    setPasswordData({...passwordData, password: e.target.value});
                    setPasswordError("");
                  }}
                  placeholder={t('new_password')}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('confirm_password')}</Form.Label>
                <Form.Control
                  type="password"
                  value={passwordData.password_confirmation}
                  onChange={(e) => {
                    setPasswordData({...passwordData, password_confirmation: e.target.value});
                    setPasswordError("");
                  }}
                  placeholder={t('confirm_password')}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowPasswordModal(false);
                setPasswordError("");
                setPasswordData({ password: '', password_confirmation: '' });
              }}
              disabled={loading}
            >
              {t('cancel')}
            </Button>
            <Button variant="primary" onClick={handlePasswordChange} disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
              {loading ? t('saving') : t('save')}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Edit Profile Modal */}
        <Modal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>{t('edit_profile')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('company_name')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="company_name"
                      value={editData.company_name}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('first_name')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="firstname"
                      value={editData.firstname}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('last_name')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="lastname"
                      value={editData.lastname}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('nicname')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="nicname"
                      value={editData.nicname}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Email")}</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={editData.email}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('street_address')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="street_address"
                      value={editData.street_address}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('addressone')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="addressone"
                      value={editData.addressone}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="formPostalCode">
                    <Form.Label>{t('postal_code')}</Form.Label>
                    <LocationAutocomplete
                      name="postal_code"
                      value={editData.postal_code}
                      onChange={handleLocationChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('telphone')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="telphone"
                      value={editData.telphone}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('website')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="website"
                      value={editData.website}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('tax_number')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="tax_number"
                      value={editData.tax_number}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('local_court')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="local_court"
                      value={editData.local_court}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
  
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('responsible_person')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="responsible_person"
                      value={editData.responsible_person}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('title')}</Form.Label>
                    <Form.Select
                      name="title"
                      value={editData.title}
                      onChange={handleChange}
                    >
                      <option value="">{t('select_title')}</option>
                      <option value="Mr">Mr</option>
                      <option value="Ms">Ms</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={loading}>
              {t('Cancel')}
            </Button>
            <Button variant="primary" onClick={handleProfileChange} disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : <FaSave className="me-1" />}
              {t('save')}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Vehicle Edit Modal */}
        <Modal
          show={showVehicleEditModal}
          onHide={() => setShowVehicleEditModal(false)}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>{t('Edit')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('driver_name')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="driver_name"
                      value={vehicleForm.driver_name}
                      onChange={handleVehicleChange}
                      placeholder={t('enter_driver_name')}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('phone_number')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="phone_number"
                      value={vehicleForm.phone_number}
                      onChange={handleVehicleChange}
                      placeholder={t('enter_phone_number')}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('vehicle_number')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="vehicle_number"
                      value={vehicleForm.vehicle_number}
                      onChange={handleVehicleChange}
                      placeholder={t('enter_vehicle_number')}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('vehicle_type')}</Form.Label>
                    <Form.Select
                      name="vehicle_type"
                      value={vehicleForm.vehicle_type}
                      onChange={handleVehicleChange}
                    >
                      <option value="">{t('select_vehicle_type')}</option>
                      <option value="Caddy">Caddy</option>
                      <option value="Bus">Bus</option>
                      <option value="Maxi Bus">Maxi Bus</option>
                      <option value="Planenbus">Planenbus</option>
                      <option value="LKW (7,5t)">LKW (7,5t)</option>
                      <option value="LKW (12t)">LKW (12t)</option>
                      <option value="LKW (18t)">LKW (18t)</option>
                      <option value="LKW (40t)">LKW (40t)</option>
                      <option value="Zugmaschine">Zugmaschine</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('max_weight')} (kg)</Form.Label>
                    <Form.Control
                      type="number"
                      name="max_weight"
                      value={vehicleForm.max_weight}
                      onChange={handleVehicleChange}
                      placeholder="0"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('max_length')} (cm)</Form.Label>
                    <Form.Control
                      type="number"
                      name="max_length"
                      value={vehicleForm.max_length}
                      onChange={handleVehicleChange}
                      placeholder="0"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('max_width')} (cm)</Form.Label>
                    <Form.Control
                      type="number"
                      name="max_width"
                      value={vehicleForm.max_width}
                      onChange={handleVehicleChange}
                      placeholder="0"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('max_height')} (cm)</Form.Label>
                    <Form.Control
                      type="number"
                      name="max_height"
                      value={vehicleForm.max_height}
                      onChange={handleVehicleChange}
                      placeholder="0"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowVehicleEditModal(false)}>
              {t('cancel')}
            </Button>
            <Button variant="primary" onClick={handleSaveVehicle} disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" className="me-2" /> : <FaSave className="me-1" />}
              {t('save')}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
}